import {
  ApiAnalytics,
  ApiAnalyticsCustomizedBookedItem,
  ApiBaseData,
  ApiBaseModel,
  ApiBookedItem,
  ApiBooking,
  ApiItem,
  ApiItemType
} from '@ibe/api';

/**
 * AnalyticsService
 *
 * Creates an analyticsBookedItem with an altered version of the bookedItem as property
 * and additional custom properties. Type of the bookedItem is restored to its original parentItem type.
 * Also capable of creating an analyticsObject.
 *
 * @see     https://support.iso-gruppe.com/browse/IBEMOD-1031
 *
 * @remarks The doc comments are written according to the TSDoc notation (https://tsdoc.org/).
 *
 * @author  Güven Gökdemir  <gueven.goekdemir@isotravel.com>
 */

export abstract class AnalyticsService {
  /**
   * Decodes the idParent into an ApiBaseModel variable
   * and returns the actual idParent as a string.
   *
   * @param idParent - Encoded base64 string
   * @returns The decoded idParent from the decoded ApiBaseModel
   */
  private static decodeIdParent(idParent: string): string {
    try {
      const decodedIdParentJSON = atob(idParent);
      const decodedIdParentModel: ApiBaseModel = JSON.parse(decodedIdParentJSON);
      return decodedIdParentModel?.idParent ?? '';
    } catch (e) {
      return idParent;
    }
  }

  /**
   * Extracts the parent item of multiple items
   *
   * @param idParent - ID of the desired parent item
   * @param itemType - Additional criteria to be used to distinguish between all items
   * @param booking - BookingObject of the bookingService
   * @returns The parent item, that matches the search results
   */
  private static extractParentItem(
    idParent: string,
    itemType: ApiItemType,
    booking: ApiBooking
  ): ApiItem | undefined {
    return booking.items.find((item: ApiItem) => {
      return item.idParent === null && item.id === idParent && item.itemType === itemType;
    });
  }

  /**
   * Gets the value of a property and casts it into the specified Type T
   *
   * @param key - The key of that specific value
   * @param property - The property of which the value should be extracted from
   * @returns The value cast as Type T
   */
  private static getPropertyValueCasted<T>(key: string, property: ApiItem): T {
    type ObjectKey = keyof typeof property;
    const keyStr = key as ObjectKey;
    return property[keyStr]?.valueOf() as T;
  }

  /**
   * Creates an analyticsBookedItem, which consists of an altered bookedItem
   * and additional properties.
   *
   * @remarks The original type of the item, that has been booked is going to be set ...(line-break not supported here)
   * inside the bookedItem (from 'BookedItem' to e.g. 'ExtraService').
   * The additional properties are serviceType and category, these can be extended if needed.
   *
   * @param bookedItem - Item to be used as property
   * @param booking - BookingObject of the bookingService
   * @returns New analyticsBookedItem with the altered bookedItem as property
   */
  public static createItem(
    bookedItem: ApiBookedItem,
    booking: ApiBooking
  ): ApiAnalyticsCustomizedBookedItem | null {
    let decodedIdParent = null;

    if (bookedItem.idParent) {
      decodedIdParent = this.decodeIdParent(bookedItem.idParent);
    }

    let extractedParentItem: ApiItem | undefined;
    // Since the bookedItem sometimes only consists of one item it won't have a parent item,
    // therefore the idParent will be the same as the id.
    if (bookedItem.id === bookedItem.idParent || !decodedIdParent) {
      extractedParentItem = this.extractParentItem(bookedItem.id, bookedItem.itemType, booking);
    } else {
      extractedParentItem = this.extractParentItem(decodedIdParent, bookedItem.itemType, booking);
    }

    if (extractedParentItem) {
      bookedItem.type = extractedParentItem.type;

      const serviceType = this.getPropertyValueCasted<ApiBaseData>(
        'serviceType',
        extractedParentItem
      );
      const category = this.getPropertyValueCasted<ApiBaseData>('category', extractedParentItem);
      // Individual property request of IBEMOD-1031:
      const serviceClass: ApiBaseData = { code: bookedItem.type, description: bookedItem.itemType };

      const analyticsCustomizedBookedItem: ApiAnalyticsCustomizedBookedItem = {
        bookedItem: bookedItem,
        serviceType: serviceType,
        category: category,
        serviceClass: serviceClass
      };
      return analyticsCustomizedBookedItem;
    } else {
      return null;
    }
  }

  /**
   * Creates an analyticsObject which consists of all analyticsBookedItems
   *
   * @param booking - BookingObject of the bookingService
   * @returns New analyticsObject with bookedItems array
   */
  public static createAnalyticsObject(booking: ApiBooking | null): ApiAnalytics | null {
    if (booking && booking.bookedItems) {
      const analyticsObj: ApiAnalytics = { bookedItems: [] } as ApiAnalytics;
      booking.bookedItems.forEach((item: ApiBookedItem) => {
        const analyticsBookedItem: ApiAnalyticsCustomizedBookedItem | null = this.createItem(
          item,
          booking
        );
        if (analyticsBookedItem) {
          analyticsObj.bookedItems.push(analyticsBookedItem);
        }
      });
      return analyticsObj;
    } else {
      return null;
    }
  }
}
export default AnalyticsService;
